import {ChangeDetectorRef, Component, inject} from '@angular/core';
import {
  ActiveFiltersChangedEvent,
  CodebookFilterItem,
  EMPTY_FILTER_TREE,
  FilterItemTree,
  FilterItemValueTree,
  findInFilterTree,
  isFilterTreeEmpty,
  TableComponent
} from '@icz/angular-table';
import {
  ButtonComponent,
  FormButtonsComponent,
  LabelComponent,
  LoadingIndicatorService,
  TooltipDirective
} from '@icz/angular-essentials';
import {
  DocumentSearchService,
  DocumentsTableColumn,
  DocumentsTableComponent,
  DocumentView,
  getUnitViewSelectorOptions,
  OrganizationalStructureService,
} from '|shared';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {TranslateService} from '@ngx-translate/core';
import {
  SharedFolderFilterDefinitionSearchDatasource
} from '|modules/shared-folders/modules/shared-folder-filter-definition-dialog/shared-folder-filter-definition-search.datasource';
import {DocumentDto, FileDto} from '|api/document';
import {ConfigPropValueService} from '|ui-essl-core';
import {AsyncPipe} from '@angular/common';

export interface SharedFolderFilterDefinitionDialogData {
  searchTerm: Nullable<string>;
  activeFilters: Nullable<FilterItemTree>;
  filterValues: Nullable<FilterItemValueTree>;
}

export interface SharedFolderFilterDefinitionDialogResult {
  searchTerm: Nullable<string>;
  activeFilters: FilterItemTree;
  filterValues: FilterItemValueTree;
}

@Component({
  selector: 'icz-shared-folder-filter-definition-dialog',
  templateUrl: './shared-folder-filter-definition-dialog.component.html',
  styleUrls: ['./shared-folder-filter-definition-dialog.component.scss'],
  imports: [
    ButtonComponent,
    DocumentsTableComponent,
    FormButtonsComponent,
    LabelComponent,
    TooltipDirective,
    AsyncPipe
  ]
})
export class SharedFolderFilterDefinitionDialogComponent {
  protected loadingService = inject(LoadingIndicatorService);
  protected documentSearchService = inject(DocumentSearchService);
  protected modalRef = injectModalRef<Nullable<SharedFolderFilterDefinitionDialogResult>>();
  protected configPropValueService = inject(ConfigPropValueService);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected translateService = inject(TranslateService);
  protected orgStructureService = inject(OrganizationalStructureService);
  protected modalData = injectModalData<SharedFolderFilterDefinitionDialogData>();

  protected selectorOptions$ = getUnitViewSelectorOptions(this.orgStructureService);

  currentSearchTerm = this.modalData.searchTerm;
  activeFilters = this.modalData.activeFilters;
  filterValues = this.modalData.filterValues;

  dataSource = new SharedFolderFilterDefinitionSearchDatasource(
    this.documentSearchService
  );

  get areNoCriteriaChosen() {
    return !this.currentSearchTerm && (!this.activeFilters || isFilterTreeEmpty(this.activeFilters));
  }

  submit() {
    this.modalRef.close({
      searchTerm: this.currentSearchTerm,
      activeFilters: this.activeFilters!,
      filterValues: this.filterValues!,
    });
  }

  cancel() {
    this.modalRef.close();
  }

  searchTermChanged(searchTerm: string) {
    this.currentSearchTerm = searchTerm.trim();
  }

  activeFiltersChanged(changeEvent: ActiveFiltersChangedEvent) {
    this.activeFilters = changeEvent.filterItemTree;
    this.filterValues = changeEvent.filterItemValueTree;
  }

  docTableInitialized(table: TableComponent<DocumentDto | FileDto, number, DocumentsTableColumn>) {
    if (this.modalData.activeFilters) {
      if (table?.tableToolbarService) {
        this.modalData.activeFilters?.values.forEach(filterItem => {
          const listFilterItem = filterItem as CodebookFilterItem;
          const associatedValue = findInFilterTree(this.modalData.filterValues ?? EMPTY_FILTER_TREE, fv => fv.id === listFilterItem.id);

          if (associatedValue) {
            table.tableToolbarService.addFilterItem(associatedValue, true);
          }
        });
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  protected readonly DocumentView = DocumentView;
}
